import {
  addDoc,
  deleteDoc,
  getDoc,
  getDocs,
  onSnapshot,
  orderBy,
  query,
  runTransaction,
  serverTimestamp,
  updateDoc,
  where,
} from 'firebase/firestore';
import { type Order, type OrderLine, type OrderStatus, type OrderSource, derivePickupDate } from '../types';
import { initFirebase } from '../lib/firebase';
import { ordersCollection, orderDoc } from '../lib/firestore';
import { reportError } from '../lib/error-reporting';
import { getFunctions, httpsCallable } from 'firebase/functions';

export async function createOrder(input: {
  customerName: string;
  pickupAtISO: string;
  lines: Array<Pick<OrderLine, 'productId' | 'quantity'> & Partial<Pick<OrderLine, 'preparedQuantity' | 'options' | 'portionSizeId'>>>;
  note?: string;
  tenantId: string;
  fcmToken?: string;
  source?: OrderSource;
  marketId?: string;
}): Promise<Order> {
  if (!input.customerName?.trim()) throw new Error('Nom client requis.');
  if (!input.pickupAtISO) throw new Error('Heure de retrait requise.');
  if (!input.tenantId?.trim()) throw new Error('Tenant ID requis.');
  if (!Array.isArray(input.lines) || input.lines.length === 0) {
    throw new Error('Au moins une ligne produit est requise.');
  }
  const lines: OrderLine[] = input.lines.map((l) => ({
    productId: l.productId,
    quantity: Math.max(0, Number(l.quantity || 0)),
    preparedQuantity: Math.min(Math.max(0, Number(l.preparedQuantity ?? 0)), Math.max(0, Number(l.quantity || 0))),
    options: l.options || [],
    ...(l.portionSizeId && { portionSizeId: l.portionSizeId }),
  }));

  const pickupDate = derivePickupDate(input.pickupAtISO);
  const nowISO = new Date().toISOString();

  const { db } = initFirebase();
  // No need to wait for authentication - orders can be created without auth (public order page)

  // Create the order object for Firestore (timestamps will be added by converter)
  const newOrder: Order = {
    id: '', // Temporary - will be replaced with Firestore-generated ID
    customerName: input.customerName.trim(),
    pickupAtISO: input.pickupAtISO,
    pickupDate,
    lines,
    status: 'pending',
    paid: false,
    note: input.note?.trim() || undefined,
    fcmToken: input.fcmToken || undefined,
    createdAtISO: nowISO,
    updatedAtISO: nowISO,
    tenantId: input.tenantId.trim(),
    source: input.source ?? 'merchant', // Default to merchant since this service is used by merchant app
    marketId: input.marketId || undefined,
  };

  // addDoc returns the document reference with the generated ID
  const docRef = await addDoc(ordersCollection(db), newOrder);

  // Return the order with the Firestore-generated ID
  return {
    ...newOrder,
    id: docRef.id,
  };
}

export async function updateOrder(order: Order): Promise<void> {
  const { db, auth, currentUserPromise } = initFirebase();
  if (!auth.currentUser) {
    await currentUserPromise;
  }
  const ref = orderDoc(db, order.id);
  await updateDoc(ref, {
    customerName: order.customerName,
    pickupAtISO: order.pickupAtISO,
    pickupDate: order.pickupDate,
    lines: order.lines.map((l) => ({
      productId: l.productId,
      quantity: l.quantity,
      preparedQuantity: l.preparedQuantity,
      options: l.options || [],
      ...(l.portionSizeId && { portionSizeId: l.portionSizeId }),
      // Sale mode fields (Phase 5)
      ...(l.saleMode && { saleMode: l.saleMode }),
      ...(l.servings !== undefined && { servings: l.servings }),
      ...(l.estimatedWeight !== undefined && { estimatedWeight: l.estimatedWeight }),
      ...(l.estimatedPrice !== undefined && { estimatedPrice: l.estimatedPrice }),
      ...(l.finalWeight !== undefined && { finalWeight: l.finalWeight }),
      ...(l.finalPrice !== undefined && { finalPrice: l.finalPrice }),
    })),
    status: order.status,
    note: order.note?.trim() || null,
    updatedAt: serverTimestamp(),
  });
}

export async function setOrderStatus(id: string, status: Extract<OrderStatus, 'ready' | 'done'>): Promise<void> {
  const { db, auth, currentUserPromise } = initFirebase();
  if (!auth.currentUser) await currentUserPromise;
  const ref = orderDoc(db, id);
  await updateDoc(ref, {
    status,
    updatedAt: serverTimestamp(),
  });
}

export async function incrementPreparedLine(
  orderId: string,
  productId: string,
  delta: number
): Promise<void> {
  const { db, auth, currentUserPromise } = initFirebase();
  if (!auth.currentUser) await currentUserPromise;

  // Use Firestore transaction for atomic read-modify-write
  await runTransaction(db, async (transaction) => {
    const orderRef = orderDoc(db, orderId);
    const orderSnap = await transaction.get(orderRef);

    if (!orderSnap.exists()) {
      throw new Error('Order not found');
    }

    const order = orderSnap.data();

    // Update the specific line's prepared quantity
    const lines = order.lines.map((l) => {
      if (l.productId !== productId) return l;
      const next = Math.max(0, Math.min(l.quantity, (l.preparedQuantity || 0) + delta));
      return { ...l, preparedQuantity: next };
    });

    // Vérifier si tous les produits sont maintenant préparés
    const allPrepared = lines.every((l) => l.preparedQuantity >= l.quantity);
    const somePrepared = lines.some((l) => l.preparedQuantity < l.quantity);

    // Préparer la mise à jour
    const updates: {
      lines: OrderLine[];
      updatedAt: ReturnType<typeof serverTimestamp>;
      status?: OrderStatus;
    } = {
      lines,
      updatedAt: serverTimestamp(),
    };

    // Si tous les produits sont préparés et que la commande est en attente, la marquer comme prête
    if (allPrepared && order.status === 'pending') {
      updates.status = 'ready';
    }
    // Si au moins un produit n'est pas préparé et que la commande est prête, la repasser en attente
    else if (somePrepared && order.status === 'ready') {
      updates.status = 'pending';
    }

    transaction.update(orderRef, updates);
  });
}

export async function queryOrdersByDay(dayYYYYMMDD: string, tenantId?: string): Promise<Order[]> {
  const { db, currentUserPromise } = initFirebase();
  await currentUserPromise;

  // Build query with optional tenantId filter
  const constraints = [
    where('pickupDate', '==', dayYYYYMMDD),
    orderBy('pickupAtISO', 'asc')
  ];

  if (tenantId) {
    constraints.unshift(where('tenantId', '==', tenantId));
  }

  const q = query(ordersCollection(db), ...constraints);
  const snap = await getDocs(q);
  return snap.docs.map((d) => d.data());
}

export function onOrdersForDaySnapshot(
  dayYYYYMMDD: string,
  callback: (orders: Order[]) => void,
  tenantId?: string
): () => void {
  const { db, currentUserPromise } = initFirebase();

  let unsubscribe: (() => void) | null = null;
  let isActive = true;

  // Attendre l'authentification avant de créer la query
  currentUserPromise
    .then(() => {
      if (!isActive) return; // Si le composant s'est démonté, ne pas créer la query

      // Build query with optional tenantId filter
      const constraints = [
        where('pickupDate', '==', dayYYYYMMDD),
        orderBy('pickupAtISO', 'asc')
      ];

      if (tenantId) {
        constraints.unshift(where('tenantId', '==', tenantId));
      }

      const q = query(ordersCollection(db), ...constraints);

      unsubscribe = onSnapshot(
        q,
        (snap) => {
          const orders = snap.docs.map((d) => d.data());
          callback(orders);
        },
        (error) => {
          reportError(error, { context: 'onOrdersForDaySnapshot', dayYYYYMMDD, tenantId });
          callback([]);
        }
      );
    })
    .catch((error) => {
      reportError(error, { context: 'onOrdersForDaySnapshot.auth', dayYYYYMMDD, tenantId });
      callback([]);
    });

  // Retourner une fonction de nettoyage
  return () => {
    isActive = false;
    if (unsubscribe) {
      unsubscribe();
    }
  };
}

/**
 * Listen to active orders for a specific day (excludes 'done' orders)
 * Server-side filtering reduces data transfer and improves performance
 */
export function onActiveOrdersForDaySnapshot(
  dayYYYYMMDD: string,
  callback: (orders: Order[]) => void,
  tenantId?: string
): () => void {
  const { db, currentUserPromise } = initFirebase();

  let unsubscribe: (() => void) | null = null;
  let isActive = true;

  currentUserPromise
    .then(() => {
      if (!isActive) return;

      // Build query with status filter (server-side)
      const constraints = [
        where('pickupDate', '==', dayYYYYMMDD),
        where('status', '!=', 'done'), // Server-side filter
        orderBy('status', 'asc'), // Required for != queries
        orderBy('pickupAtISO', 'asc')
      ];

      if (tenantId) {
        constraints.unshift(where('tenantId', '==', tenantId));
      }

      const q = query(ordersCollection(db), ...constraints);

      unsubscribe = onSnapshot(
        q,
        (snap) => {
          const orders = snap.docs.map((d) => d.data());
          callback(orders);
        },
        (error) => {
          console.error('Error in onActiveOrdersForDaySnapshot:', error);
          callback([]);
        }
      );
    })
    .catch((error) => {
      reportError(error, { context: 'onActiveOrdersForDaySnapshot.auth', dayYYYYMMDD, tenantId });
      callback([]);
    });

  return () => {
    isActive = false;
    if (unsubscribe) {
      unsubscribe();
    }
  };
}

export async function deleteOrder(id: string): Promise<void> {
  const { db, auth, currentUserPromise } = initFirebase();
  if (!auth.currentUser) await currentUserPromise;
  const ref = orderDoc(db, id);
  await deleteDoc(ref);
}

export async function updateOrderStatus(id: string, status: OrderStatus): Promise<void> {
  const { db, auth, currentUserPromise } = initFirebase();
  if (!auth.currentUser) await currentUserPromise;
  const ref = orderDoc(db, id);
  await updateDoc(ref, {
    status,
    updatedAt: serverTimestamp(),
  });
}

export async function toggleOrderPaid(id: string, paid: boolean): Promise<void> {
  const { db, auth, currentUserPromise } = initFirebase();
  if (!auth.currentUser) await currentUserPromise;
  const ref = orderDoc(db, id);
  await updateDoc(ref, {
    paid,
    updatedAt: serverTimestamp(),
  });
}

/**
 * Update the final weight and price for a specific order line
 * Used by merchant to record actual weight after weighing
 * @param orderId The order ID
 * @param lineIndex The index of the line to update
 * @param finalWeight The final weight in kg
 * @param finalPrice The final price in euros
 */
export async function updateLineWeight(
  orderId: string,
  lineIndex: number,
  finalWeight: number,
  finalPrice: number
): Promise<void> {
  const { db, auth, currentUserPromise } = initFirebase();
  if (!auth.currentUser) await currentUserPromise;

  // Use Firestore transaction for atomic read-modify-write
  await runTransaction(db, async (transaction) => {
    const orderRef = orderDoc(db, orderId);
    const orderSnap = await transaction.get(orderRef);

    if (!orderSnap.exists()) {
      throw new Error('Order not found');
    }

    const order = orderSnap.data();

    if (lineIndex < 0 || lineIndex >= order.lines.length) {
      throw new Error('Invalid line index');
    }

    // Update the specific line with final weight and price
    const lines = order.lines.map((l, i) => {
      if (i !== lineIndex) return l;
      return {
        ...l,
        finalWeight,
        finalPrice,
      };
    });

    transaction.update(orderRef, {
      lines,
      updatedAt: serverTimestamp(),
    });
  });
}

/**
 * Validate an order (accept it) by changing status from 'awaiting_validation' to 'pending'
 * This triggers a push notification to the customer via Cloud Function
 * @param orderId The order ID to validate
 */
export async function validateOrder(orderId: string): Promise<void> {
  const { db, auth, currentUserPromise } = initFirebase();
  if (!auth.currentUser) await currentUserPromise;
  const ref = orderDoc(db, orderId);
  await updateDoc(ref, {
    status: 'pending',
    updatedAt: serverTimestamp(),
  });
}

/**
 * Get a single order by its ID
 * Used by customer app to fetch order status
 */
export async function getOrderById(orderId: string): Promise<Order | null> {
  const { db, auth, currentUserPromise } = initFirebase();
  if (!auth.currentUser) await currentUserPromise;
  const ref = orderDoc(db, orderId);
  const snap = await getDoc(ref);

  if (!snap.exists()) {
    return null;
  }

  return snap.data();
}

/**
 * Listen to a single order in real-time
 * Used by customer app to track order status changes
 * @param orderId The order ID to watch
 * @param callback Called whenever the order changes
 * @param onError Optional error callback for permission/network errors
 * @returns Unsubscribe function
 */
export function onOrderSnapshot(
  orderId: string,
  callback: (order: Order | null) => void,
  onError?: (error: Error) => void
): () => void {
  const { db, currentUserPromise } = initFirebase();

  let unsubscribe: (() => void) | null = null;
  let isActive = true;

  // Wait for auth before creating the listener (same pattern as onOrdersForDaySnapshot)
  currentUserPromise
    .then(() => {
      if (!isActive) return;

      const ref = orderDoc(db, orderId);
      unsubscribe = onSnapshot(
        ref,
        (snap) => {
          if (snap.exists()) {
            callback(snap.data());
          } else {
            callback(null);
          }
        },
        (error) => {
          reportError(error, { context: 'onOrderSnapshot', orderId });
          if (onError) {
            onError(error);
          } else {
            callback(null);
          }
        }
      );
    })
    .catch((error) => {
      reportError(error, { context: 'onOrderSnapshot.auth', orderId });
      if (onError) {
        onError(error);
      } else {
        callback(null);
      }
    });

  return () => {
    isActive = false;
    if (unsubscribe) {
      unsubscribe();
    }
  };
}

/**
 * Order source statistics
 */
export interface OrderSourceStats {
  customer: number;
  merchant: number;
  unknown: number;
  total: number;
}

/**
 * Order source statistics response (includes unknown order IDs for debugging)
 */
export interface OrderSourceStatsResponse {
  stats: OrderSourceStats;
  unknownOrderIds: string[];
}

/**
 * Get order source statistics for a tenant
 * Calls the getOrderSourceStats Cloud Function
 * @param tenantId The tenant ID to get stats for
 * @returns Promise with the stats and unknown order IDs
 */
export async function getOrderSourceStats(tenantId: string): Promise<OrderSourceStatsResponse> {
  const { app } = initFirebase();
  const functions = getFunctions(app, 'europe-west1');
  const getStatsFn = httpsCallable<{ tenantId: string }, { success: boolean; stats: OrderSourceStats; unknownOrderIds: string[] }>(
    functions,
    'getOrderSourceStats'
  );

  const result = await getStatsFn({ tenantId });
  return {
    stats: result.data.stats,
    unknownOrderIds: result.data.unknownOrderIds || []
  };
}
